import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { ImageUploader } from '../ImageUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
          <X className="w-6 h-6" />
        </button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder, multiline = false }) {
  const [activeLang, setActiveLang] = useState('it');
  const Component = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (
            <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.flag}</TabsTrigger>
          ))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Component value={value?.[lang.code] || ''} onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })}
              placeholder={`${placeholder} (${lang.name})`} rows={multiline ? 4 : undefined} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// ==================== ADMIN TEAM ====================
export function AdminTeam() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchMembers();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${API}/team?active_only=false`);
      setMembers(res.data);
    } catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editingMember.id && members.find(m => m.id === editingMember.id)) {
        await axios.put(`${API}/team/${editingMember.id}`, editingMember);
        toast.success('Membro aggiornato');
      } else {
        await axios.post(`${API}/team`, editingMember);
        toast.success('Membro creato');
      }
      setShowDialog(false);
      fetchMembers();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo membro?')) return;
    try {
      await axios.delete(`${API}/team/${id}`);
      toast.success('Membro eliminato');
      fetchMembers();
    } catch (error) { toast.error('Errore nell\'eliminazione'); }
  };

  const openNew = () => {
    setEditingMember({
      name: '',
      role: { it: '', en: '', fr: '', de: '', es: '' },
      bio: { it: '', en: '', fr: '', de: '', es: '' },
      photo_url: '',
      social_links: {},
      is_active: true
    });
    setShowDialog(true);
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar currentPath="/admin/team" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Team</h1>
          <Button onClick={openNew} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Nuovo Membro</Button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.map((member) => (
                <div key={member.id} className="bg-white rounded-lg border overflow-hidden">
                  <div className="aspect-square bg-gray-100">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Users className="w-16 h-16" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg">{member.name}</h3>
                    <p className="text-[#FF6D00] text-sm">{member.role?.it || ''}</p>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => { setEditingMember(member); setShowDialog(true); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(member.id)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingMember?.id ? 'Modifica Membro' : 'Nuovo Membro'}</DialogTitle></DialogHeader>
          {editingMember && (
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nome</label>
                <Input value={editingMember.name} onChange={(e) => setEditingMember({...editingMember, name: e.target.value})} />
              </div>
              <TranslatedInput label="Ruolo" value={editingMember.role} onChange={(v) => setEditingMember({...editingMember, role: v})} placeholder="es. Presidente" />
              <TranslatedInput label="Bio" value={editingMember.bio} onChange={(v) => setEditingMember({...editingMember, bio: v})} placeholder="Breve descrizione" multiline />
              <div>
                <label className="block text-sm font-medium mb-2">URL Foto</label>
                <Input value={editingMember.photo_url} onChange={(e) => setEditingMember({...editingMember, photo_url: e.target.value})} placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Social Links</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Facebook URL" value={editingMember.social_links?.facebook || ''} 
                    onChange={(e) => setEditingMember({...editingMember, social_links: {...editingMember.social_links, facebook: e.target.value}})} />
                  <Input placeholder="Instagram URL" value={editingMember.social_links?.instagram || ''} 
                    onChange={(e) => setEditingMember({...editingMember, social_links: {...editingMember.social_links, instagram: e.target.value}})} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editingMember.is_active} onChange={(e) => setEditingMember({...editingMember, is_active: e.target.checked})} />
                <label className="text-sm">Membro attivo</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
